import type { GetRowIdParams } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { productsBlotterApiConnector } from 'apiConnector/blotters/productsBlotter';
import { PageLessFooter } from 'components/Blotters/pageless-footer';
import { useTradeBlotterServerSideGridOptions } from 'components/Blotters/Trades/Grid/hooks/useTradeBlotterServerSideGridOptions';
import { useTradesBlotterState } from 'components/Blotters/Trades/Grid/hooks/useTradesBlotterState';
import { SensitivityDetailCellRenderer } from 'components/Blotters/Trades/Grid/sensitivity-detail-cell-renderer';
import { ExternalFilter } from 'components/Blotters/Trades/Grid/utils';
import { CreditCheckBadge } from 'components/shared/Blotter/CreditCheckBadge/CreditCheckBadge.connect';
import { ServerSideBlotterGrid } from 'components/shared/Blotter/Grid/blotter-serverside';
import { useGetFilterValuesWithApi } from 'components/shared/Blotter/Grid/Hooks/useTranslateFilterInColumnDefinitions';
import { canSetActive, getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { TradesOrProductsBlotterRequest } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { useAppSelector } from 'hooks/useAppSelector';
import type { IBlotterProductItem } from 'models/IBlotterItem';
import { useState } from 'react';
import { getIsUserInternal } from 'state/selectors';
import { httpClient } from 'utils/http/httpClient';

const modules = [MasterDetailModule];

export type ProductsBlotterServerSideGridProps = {};

function getRowId({ data }: GetRowIdParams) {
  return data['productId'];
}

const productsBlotterApi = productsBlotterApiConnector(httpClient);

const searchItems = (req: ServerSideBlotterSearchRequest) => productsBlotterApi.serverSideSearch(req);
const getHeaderIntlKey = getHeaderForBlotterIntlKey('tradesBlotter');
type Props = { setLastBlotterRequest: (value: ServerSideBlotterSearchRequest) => void };

const MaxItemsCount = 10_000;

export function ProductsBlotterServerSideGrid({ setLastBlotterRequest }: Props) {
  const getFilterValuesWithDispatch = useGetFilterValuesWithApi(productsBlotterApi);

  const {
    columnDefinitions,
    blotterRequest,
    columnState,
    columnGroupState,
    pageSizeState,
    isBlotterRefreshable,
    onBlotterColumnStateChanged,
    onSetActiveItem,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    onPageSizeChanged,
  } = useTradesBlotterState('products');
  const isInternalUser = useAppSelector(getIsUserInternal);
  const gridOptions = useTradeBlotterServerSideGridOptions<'products', ExternalFilter>({
    columnDefinitions,
    blotterRequest,
    columnState,
    columnGroupState,
    pageSizeState,
    isBlotterRefreshable,
    onBlotterColumnStateChanged,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    onPageSizeChanged,
    setLastBlotterRequest,
    searchItems,
    getFilterValues: getFilterValuesWithDispatch,
    getHeaderIntlKey,
  });

  const [isTruncated, setIsTruncated] = useState(false);

  return (
    <>
      <ServerSideBlotterGrid<TradesOrProductsBlotterRequest, IBlotterProductItem>
        name="tradesBlotter"
        logAnalyticName="Blotter My Products"
        masterDetail
        detailCellRenderer={SensitivityDetailCellRenderer}
        detailRowAutoHeight
        e2e="product-blotter"
        canSetActive={canSetActive}
        getRowId={getRowId}
        modules={modules}
        onSetActiveItem={onSetActiveItem}
        {...gridOptions}
        keepDetailRows
        useNativePagination
        maxRowsCount={MaxItemsCount}
        setIsTruncated={setIsTruncated}
      />
      <PageLessFooter maxItemsCount={MaxItemsCount} isTruncated={isTruncated}>
        {isInternalUser && <CreditCheckBadge />}
      </PageLessFooter>
    </>
  );
}
